<script setup>
import { computed } from "vue";
import { storeToRefs } from "pinia";
import {
  StealieHeading,
  StealieButton,
  StealieTabs,
  StealieTabPanel,
} from "@get-overalls/fe-stealie";
import { Squares2X2Icon } from "@heroicons/vue/24/outline";
import { useRequestsStore } from "@/stores/requests";
import { useRequestSurveyStore } from "@/stores/requestSurvey";
import { useUserStore } from "@/stores/user";
import AuthenticatedLayout from "@/layouts/AuthenticatedLayout.vue";
import ContentPanel from "@/components/ContentPanel.vue";
import RequestRow from "@/components/RequestRow.vue";
import SuggestedRequestsForCategory from "@/components/SuggestedRequestsForCategory.vue";

const userStore = useUserStore();
const { user } = storeToRefs(userStore);
const requestsStore = useRequestsStore();
const {
  inProgressRequests,
  openRequests,
  openLoaded,
  inProgressLoaded,
  suggestedRequests,
} = storeToRefs(requestsStore);
const requestsLoaded = computed(() => inProgressLoaded.value && openLoaded.value);

const tabs = computed(() => suggestedRequests.value.map((category) => category.name));

const requestSurveyStore = useRequestSurveyStore();
const handleStartRequest = (request) => {
  requestSurveyStore.beginRequestSurvey(request);
};

const highlightedRequest = computed(() => {
  if (inProgressRequests.value.length > 0) {
    return inProgressRequests.value[0];
  }
  if (openRequests.value.length > 0) {
    return openRequests.value[0];
  }
  return null;
});
</script>

<template>
  <AuthenticatedLayout>
    <ContentPanel v-if="user?.request_count !== 0">
      <StealieOverline class="text-xl !tracking-normal !normal-case">
        Welcome, {{ user?.first_name }}!
      </StealieOverline>
      <template v-if="requestsLoaded">
        <StealieHeading
          v-if="inProgressRequests.length > 0"
          class="!text-[2.25rem] text-pretty xl:!text-[2.5rem]"
          level="1"
          data-sr-unmask
        >
          Your request is in progress
        </StealieHeading>
        <StealieHeading
          v-else-if="openRequests.length > 0"
          class="!text-[2.25rem] text-pretty xl:!text-[2.5rem]"
          level="1"
          data-sr-unmask
        >
          Your latest request
        </StealieHeading>
        <StealieHeading
          v-else
          level="1"
          class="!text-[2rem] xl:!text-[2.5rem]"
          data-sr-unmask
        >
          You have no open requests
        </StealieHeading>
        <RequestRow
          v-if="highlightedRequest"
          class="mt-8"
          :request="highlightedRequest"
          :category-image-url="highlightedRequest.survey_category_image_url"
          :category-name="highlightedRequest.survey_category_name"
        />
      </template>
    </ContentPanel>
    <ContentPanel>
      <div v-if="user?.request_count === 0">
        <StealieOverline class="text-xl !tracking-normal !normal-case"
          >Welcome, {{ user?.first_name }}!</StealieOverline
        >
        <StealieHeading level="2" data-sr-unmask>It's your first request!</StealieHeading>
        <StealieParagraph size="lg" class="mt-2 text-pretty" data-sr-unmask>
          Creating your first request is easy! Start with a custom request or choose from
          popular options to get started.
        </StealieParagraph>
        <p class="mt-6" data-sr-unmask>
          <StealieButton
            variant="tertiary"
            class="*:!flex *:items-center *:gap-1.5"
            @click="requestSurveyStore.openCategoryDrawer('explore')"
          >
            <Squares2X2Icon class="size-6" /> Explore categories
          </StealieButton>
        </p>
      </div>
      <div
        v-else
        class="mb-8 flex flex-col justify-between gap-y-8 sm:flex-row"
        data-sr-unmask
      >
        <StealieHeading level="2" size="lg">Request ideas</StealieHeading>
        <p class="">
          <StealieButton
            variant="tertiary"
            class="*:!flex *:items-center *:gap-1.5"
            @click="requestSurveyStore.openCategoryDrawer('explore')"
          >
            <Squares2X2Icon class="size-6" /> Explore categories
          </StealieButton>
        </p>
      </div>
      <div
        class="-mx-4 hidden justify-center divide-x divide-[#edeff0] *:px-4 sm:flex 2xl:-mx-12 2xl:*:px-12"
        data-sr-unmask
      >
        <template v-for="(category, index) in suggestedRequests" :key="category.id">
          <SuggestedRequestsForCategory
            v-if="index < 3"
            :class="{
              'hidden xl:block': index === 2,
              'border-r-0 xl:border-r': index === 1,
            }"
            :category="{
              id: category.id,
              name: category.name,
              imageUrl: category.image_url,
            }"
            :requests="category.request_surveys"
            @start-request="handleStartRequest"
          />
        </template>
      </div>
      <div class="mt-4 block sm:hidden" data-sr-unmask>
        <StealieTabs :tabs="tabs">
          <StealieTabPanel
            v-for="category in suggestedRequests"
            :key="category.id"
            :value="category.name"
            class="pt-8"
          >
            <SuggestedRequestsForCategory
              :category="{
                id: category.id,
                name: category.name,
                imageUrl: category.image_url,
              }"
              :requests="category.request_surveys"
              hide-header
              @start-request="handleStartRequest"
            />
          </StealieTabPanel>
        </StealieTabs>
      </div>
      <hr class="-mx-4 mt-11 mb-8 h-px border-[#edeff0] sm:-mx-8" />
      <section
        class="relative flex flex-col items-center justify-between gap-y-4 md:flex-row"
        data-sr-unmask
      >
        <img
          src="/illustrations/raised-hands.svg"
          class="absolute -bottom-8 left-[43%] z-0 hidden w-[25%] max-w-64 xl:block"
        />
        <StealieHeading
          level="3"
          size="lg"
          class="text-center text-[26px] text-balance sm:text-left sm:text-3xl"
          >Don't see what you need?</StealieHeading
        >
        <StealieButton @click="requestSurveyStore.openCategoryDrawer()">
          Create a request
        </StealieButton>
      </section>
    </ContentPanel>
  </AuthenticatedLayout>
</template>
